<script setup lang="ts">
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import VAdminFeesCountriesWidget from '@modules/admin-fees/components/VAdminFeesCountriesWidget/VAdminFeesCountriesWidget.vue';
import VAdminFeesMerchantsWidget from '@modules/admin-fees/components/VAdminFeesMerchantsWidget/VAdminFeesMerchantsWidget.vue';
import VCountryFeesSettingsDialog from '@modules/admin-fees/components/VCountryFeesSettingsDialog/VCountryFeesSettingsDialog.vue';
import { AFFILIATE_PROGRAM_ADMIN_FEES_PERMISSIONS, TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES } from '@modules/admin-fees/constants';
import VChangeHistory from '@modules/change-history/components/VChangeHistory/VChangeHistory.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';

import {
  useAdminFeesCommon,
  useAdminFeesForm,
  useAdminFeesMerchantAndCountryFilters,
  useHistoryOfAdminFees,
} from './composables';

const {
  activeFilterMerchantId,
  countryFeesSettingsDialog,
  selectedCountryFeesFilterIndex,
  formPayload,
  isGreaterThenSm,
  adminFeesData,
  isShowLoading,
  merchantList,
  countriesList,
  isEdit,
  editFromPermission,
  productsKeyList,
  warnMessage,
  onCountryFilterEdit,
  onSelectMerchant,
} = useAdminFeesCommon();

const {
  merchantsOptions,
  invalidAdminFeesFilters,
  adminFeesMerchantTabs,
  filteredCountryFees,
  displayFilterErrors,
  onConfirmRemove,
  onMerchantFilterRemove,
  onCountryFilterRemove,
  onAddMerchant,
  onAddCountryFilter,
  onCountryFeesSave,
} = useAdminFeesMerchantAndCountryFilters(
  formPayload,
  merchantList,
  countriesList,
  activeFilterMerchantId,
);

const {
  formSchema,
  formGroups,
  isSaveLoading,
  errors,
  clearError,
  onFormChange,
  onSubmit,
} = useAdminFeesForm(
  formPayload,
  productsKeyList,
  editFromPermission,
  isShowLoading,
  invalidAdminFeesFilters,
  isEdit,
  adminFeesData,
);

const {
  historyOfAdminFeesChanges,
  isHistoryLoading,
  onHistoryDelete,
} = useHistoryOfAdminFees();
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminFeesSettingsPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'ADMIN_FEES_HEADER')"
  >
    <VForm
      :schema="formSchema"
      :groups="formGroups"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'FORM_SAVE')"
      :is-loading="isSaveLoading"
      :disabled="!editFromPermission"
      :custom-errors="errors"
      :is-buttons-centered="!isGreaterThenSm"
      :is-group-title-centered="!isGreaterThenSm"
      @change="onFormChange"
      @submit="onSubmit"
      @interacted="clearError"
    >
      <template #admin_fees_merchant_filters="{ className, readonly }">
        <VAdminFeesMerchantsWidget
          :merchant-tabs="adminFeesMerchantTabs"
          :merchant-options="merchantsOptions"
          :class="className"
          :readonly="readonly || !editFromPermission"
          :errors="displayFilterErrors"
          @select="onSelectMerchant"
          @add="onAddMerchant"
          @remove="onConfirmRemove($event, onMerchantFilterRemove)"
        />
      </template>
      <template #admin_fees_country_filters="{ className, readonly }">
        <VAdminFeesCountriesWidget
          :countries-cards="filteredCountryFees"
          :readonly="readonly || !editFromPermission"
          :class="className"
          @add="onAddCountryFilter"
          @edit="onCountryFilterEdit"
          @remove="onConfirmRemove($event, onCountryFilterRemove)"
        />
      </template>
      <template #prefooter>
        <VMessage
          v-if="warnMessage"
          class="input-max-width"
          severity="warn"
        >
          {{ warnMessage }}
        </VMessage>
      </template>
    </VForm>
  </VCard>

  <VLoader
    :loading="isHistoryLoading"
    class="mt-4"
  >
    <VChangeHistory
      :list="historyOfAdminFeesChanges"
      :delete-permission="AFFILIATE_PROGRAM_ADMIN_FEES_PERMISSIONS.DELETE"
      @delete="onHistoryDelete"
    />
  </VLoader>

  <VCountryFeesSettingsDialog
    v-model:visible="countryFeesSettingsDialog"
    :country-fees-filters="filteredCountryFees"
    :selected-country-fees-index="selectedCountryFeesFilterIndex"
    :countries="countriesList"
    :readonly="!editFromPermission"
    @save="onCountryFeesSave"
  />
</template>
